import React from 'react';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { TitleSection, Breadcrumb, H1, Section } from '@shortlyster/ui-kit';
import { Box } from '@compono/ui';
import { useParams } from 'react-router-dom';
import Link from '../../../components/Link';
import Container from 'components/Container';
import {
  OrganisationQuery,
  organisationQuery,
  SsoConnection,
  UpdateSSOConnection,
  RemoveSSOConnection,
  Sso,
} from '../queries';
import OrganisationSsoSettingsForm, { NewSso } from './OrganisationSsoSettingsForm';
import { showAlert } from 'src/components/AlertUtils';
import { useApolloClients } from 'src/lib/auth/AuthorizedApolloProvider';
import history from 'lib/history';

const OrganisationSsoDetail: React.FC = () => {
  const { id: orgId } = useParams<{ id: string }>();
  const { ssoId } = useParams<{ ssoId: string }>();
  const { federationGatewayClient } = useApolloClients()!;

  const { data: orgQueryData, loading: orgQueryLoading } = useQuery<OrganisationQuery>(
    organisationQuery,
    {
      variables: { id: orgId },
      fetchPolicy: 'cache-and-network',
    }
  );
  const {
    data: ssoData,
    loading: ssoDataloading,
    refetch: refetchSso,
  } = useQuery<Sso>(SsoConnection, {
    variables: { id: ssoId, organisationId: orgId },
    client: federationGatewayClient,
    fetchPolicy: 'cache-and-network',
  });
  const [updateSsoConnection, { loading: updateLoading, error: updateError }] = useMutation(
    UpdateSSOConnection,
    {
      client: federationGatewayClient,
    }
  );

  const [removeSsoConnection, { loading: removeLoading, error: removeError }] = useMutation(
    RemoveSSOConnection,
    {
      client: federationGatewayClient,
    }
  );
  if (orgQueryLoading || ssoDataloading) return <p>Loading...</p>;
  const organisation = orgQueryData?.organisations[0];
  const organisationId = organisation && organisation.id;
  const onSubmit = async (formData: NewSso, id?: string) => {
    const payload: any = {
      id: id || formData.id,
      organisationId,
      enabled: formData.enabled,
      name: formData.name,
      type: formData.type,
      encryptedResponses: formData.encryptedResponses,
      autoSignup: formData.autoSignup,
      replaceLoginPage: formData.replaceLoginPage,
    };

    if (formData.metadataFile) {
      payload.metadataFile = formData.metadataFile;
    } else if (formData.metadataUrl) {
      payload.metadataUrl = formData.metadataUrl;
    }

    if (formData.attributeMapping) {
      payload.attributeMapping = formData.attributeMapping;
    }

    try {
      await updateSsoConnection({
        variables: {
          input: payload,
        },
      });
      await refetchSso();
      showAlert({
        title: 'SSO settings updated',
        content: 'SSO settings have been updated.',
        alertType: 'success',
      });
    } catch (e) {
      console.log(`ERROR: ${e}`);
      await refetchSso();
      showAlert({
        title: 'Error while updating SSO settings',
        content: 'SSO settings could not be updated.',
        alertType: 'error',
      });
    }
  };
  const onDelete = async (id: string) => {
    try {
      await removeSsoConnection({
        variables: {
          id,
          organisationId,
        },
      });
      showAlert({
        title: 'SSO settings deleted',
        content: 'SSO settings have been deleted.',
        alertType: 'success',
      });
      history.changeDomainTo(`/organisations/${orgId}/sso`);
    } catch (e) {
      showAlert({
        title: 'Error while deleting SSO settings',
        content: 'SSO settings could not be deleted.',
        alertType: 'error',
      });
    }
  };

  return (
    <Container>
      <TitleSection>
        <Breadcrumb>
          <Link to="/organisations">Organisations</Link>
          <Link to={`/organisations/${orgId}/sso`}>{organisation?.name}</Link>
          <Link to="" aria-current={true}>
            Edit SSO Connection
          </Link>
        </Breadcrumb>
        <H1>Edit SSO Connection</H1>
      </TitleSection>
      <Section>
        <Box
          sx={{
            padding: 4,
            width: ['100%'],
          }}
        >
          <OrganisationSsoSettingsForm
            onSubmit={onSubmit}
            defaultValue={ssoData.ssoConnection}
            onDelete={onDelete}
            saving={updateLoading}
            orgId={orgId}
          />
        </Box>
      </Section>
    </Container>
  );
};

export default OrganisationSsoDetail;
